'use client'

import { useState } from 'react'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { reloadPage } from '@marketplace-web/shared/browser-util'
import { ConversationActionProps } from '@marketplace-web/messaging/conversation-action-data'
import { ConversationActionButton } from '@marketplace-web/messaging/conversation-action-feature'
import { TransactionSide } from '@marketplace-web/messaging/conversation-messaging-data'
import {
  resolveComplaint,
  clickEvent,
  selfServiceViewScreenEvent,
} from '@marketplace-web/complaints/complaints-data'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { ResponseCode } from '@marketplace-web/core-api/api-client-util'

import ResolveIssueModal from './ResolveIssueModal'
import InsufficientBalanceModal from './InsufficientBalanceModal'

const INSUFFICIENT_BALANCE_ERROR = 'wallet_amount'

type Props = ConversationActionProps

enum ResolveComplaintModal {
  ResolveIssue,
  InsufficientBalance,
}

const ResolveComplaint = ({ primary, title, inverse, theme, size }: Props) => {
  const { track } = useTracking()

  const { transaction } = useConversationContext()

  const transactionId = transaction?.id
  const complaintId = transaction?.complaintId

  const [visibleModalName, setVisibleModalName] = useState<ResolveComplaintModal | null>(null)

  const handleResolveIssueClick = () => {
    setVisibleModalName(ResolveComplaintModal.ResolveIssue)
  }

  const handleModalClose = () => {
    setVisibleModalName(null)
  }

  const trackInsufficientBalanceModalScreen = () => {
    if (!transactionId) return

    track(
      selfServiceViewScreenEvent({
        screen: 'insufficient_balance_modal',
        transactionId,
      }),
    )
  }

  const confirmComplaintResolution = async () => {
    const targetDetails = { transaction_id: transactionId }

    track(
      clickEvent({
        target: 'confirm_resolve_this_issue',
        targetDetails: JSON.stringify(targetDetails),
      }),
    )

    if (!complaintId) return

    const response = await resolveComplaint(complaintId)

    if ('errors' in response) {
      if (
        response.code === ResponseCode.ValidationError &&
        response.errors[0]!.field === INSUFFICIENT_BALANCE_ERROR &&
        transaction?.userSide === TransactionSide.Seller
      ) {
        setVisibleModalName(ResolveComplaintModal.InsufficientBalance)
        trackInsufficientBalanceModalScreen()
      }

      return
    }

    reloadPage()
  }

  if (!transactionId) return null

  return (
    <>
      <ConversationActionButton
        text={title}
        primary={primary}
        size={size}
        theme={theme}
        inverse={inverse}
        onClick={handleResolveIssueClick}
      />
      <span data-testid="resolve-modal">
        <ResolveIssueModal
          isOpen={visibleModalName === ResolveComplaintModal.ResolveIssue}
          onSubmit={confirmComplaintResolution}
          onClose={handleModalClose}
        />
      </span>
      <InsufficientBalanceModal
        isOpen={visibleModalName === ResolveComplaintModal.InsufficientBalance}
        transactionId={transactionId}
        onClose={handleModalClose}
      />
    </>
  )
}

export default ResolveComplaint
